import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Check, Phone, Sparkles } from "lucide-react";
import { useI18n } from "../lib/i18n";
import SEO from "../components/SEO";

export default function Subscriptions() {
  const { lang } = useI18n();
  const isAr = lang === "ar";

  const plans = useMemo(() => [
    {
      key: "individual",
      nameAr: "الفرد", nameEn: "Individual",
      priceKwd: 20, periodAr: "/شهرياً", periodEn: "/month",
      tagAr: "للأفراد والعائلات", tagEn: "Individuals & families",
      featuresAr: [
        "محادثة غير محدودة مع المستشار الذكي سامي",
        "مراجعة 3 مستندات شهرياً",
        "خصم 20% على الاستشارات الشخصية",
        "إشعارات الجلسات بالواتساب",
      ],
      featuresEn: [
        "Unlimited Sami AI chat",
        "Review up to 3 documents/month",
        "20% off personal consultations",
        "WhatsApp hearing reminders",
      ],
    },
    {
      key: "small-business",
      nameAr: "شركات صغيرة", nameEn: "Small business",
      priceKwd: 75, periodAr: "/شهرياً", periodEn: "/month",
      tagAr: "حتى 10 موظفين", tagEn: "Up to 10 employees",
      popular: true,
      featuresAr: [
        "كل ميزات الفرد",
        "مراجعة 10 مستندات شهرياً",
        "صياغة عقدين كل شهر",
        "خط مباشر مع محامي مكتب الدخيل",
        "خصم 30% على القضايا",
      ],
      featuresEn: [
        "Everything in Individual",
        "Review 10 documents/month",
        "Draft 2 contracts/month",
        "Direct line to a firm lawyer",
        "30% off litigation cases",
      ],
    },
    {
      key: "medium-business",
      nameAr: "شركات متوسطة", nameEn: "Medium business",
      priceKwd: 200, periodAr: "/شهرياً", periodEn: "/month",
      tagAr: "10–100 موظف", tagEn: "10–100 employees",
      featuresAr: [
        "كل ميزات الشركات الصغيرة",
        "مراجعة 30 مستنداً شهرياً",
        "صياغة 5 عقود شهرياً",
        "تمثيل في 3 جلسات شهرياً",
        "مدير حساب مخصص",
      ],
      featuresEn: [
        "Everything in Small business",
        "Review 30 documents/month",
        "Draft 5 contracts/month",
        "3 court appearances/month",
        "Dedicated account manager",
      ],
    },
    {
      key: "contract-review",
      nameAr: "مراجعة عقود", nameEn: "Contract review",
      priceKwd: 50, periodAr: "/عقد", periodEn: "/contract",
      tagAr: "حسب الحاجة", tagEn: "On demand",
      featuresAr: [
        "مراجعة عقد كامل خلال 48 ساعة",
        "تقرير تفصيلي بالملاحظات والتعديلات",
        "اتصال مع المحامي للنقاش",
        "تعديل وإعادة صياغة عند الحاجة",
      ],
      featuresEn: [
        "Full contract review within 48 hours",
        "Detailed observations & redlines",
        "Lawyer follow-up call",
        "Re-drafting on request",
      ],
    },
    {
      key: "monthly-retainer",
      nameAr: "اشتراك مكتب شهري", nameEn: "Monthly retainer",
      priceKwd: 350, periodAr: "/شهرياً", periodEn: "/month",
      tagAr: "للمشاريع الكبيرة", tagEn: "Large operations",
      featuresAr: [
        "محامٍ مخصص للمنشأة",
        "اجتماعات أسبوعية",
        "صياغة ومراجعة غير محدودة",
        "تمثيل قانوني شامل (يخصم من القضايا)",
        "تقارير امتثال شهرية",
      ],
      featuresEn: [
        "Dedicated firm lawyer",
        "Weekly meetings",
        "Unlimited drafting & review",
        "Full legal representation (credited)",
        "Monthly compliance reports",
      ],
    },
  ], []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <SEO
        titleAr="الاشتراكات — مجموعة الدخيل القانونية"
        titleEn="Subscriptions — Aldakheel Legal Group"
        descriptionAr="أربع خطط اشتراك شهرية للأفراد والشركات، وخدمة مراجعة عقد منفردة."
        descriptionEn="Four monthly subscription plans for individuals and businesses, plus a one-off contract review."
      />

      <div className="grid lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-7">
          <p className="eyebrow inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--gold-deep)" }} />
            {isAr ? "الاشتراكات" : "Subscriptions"}
          </p>
          <h1 className="font-display text-5xl lg:text-6xl mt-3 leading-[1.05] tracking-tight">
            {isAr ? "خطط تناسب كل تطلّع قانوني." : "Plans for every legal need."}
          </h1>
        </div>
        <div className="lg:col-span-5 lg:pt-3">
          <p className="text-lg text-slate-600 leading-relaxed">
            {isAr
              ? "اختر الخطة الأنسب لك أو لشركتك. كل الباقات تشمل وصولاً كاملاً للمكتبة القانونية، مستشار سامي الذكي، ودعم محامٍ مرخّص من المكتب."
              : "Pick the right plan for you or your business. Every plan includes full access to our law library, Sami AI, and a licensed firm lawyer."}
          </p>
        </div>
      </div>

      {/* PLANS GRID */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {plans.map((p) => (
          <div
            key={p.key}
            data-testid={`plan-${p.key}`}
            className={`relative p-7 border bg-white flex flex-col ${
              p.popular ? "border-[var(--gold-deep)]" : ""
            }`}
            style={{ borderColor: p.popular ? "var(--gold-deep)" : "var(--line)" }}
          >
            {p.popular && (
              <span
                className="absolute -top-3 right-6 px-3 py-0.5 text-[10px] uppercase tracking-[0.2em] font-medium rounded-sm"
                style={{ background: "var(--gold-deep)", color: "#fff" }}
              >
                {isAr ? "الأكثر طلباً" : "Most popular"}
              </span>
            )}
            <p className="eyebrow" style={{ color: "var(--gold-deep)" }}>
              {isAr ? p.tagAr : p.tagEn}
            </p>
            <h3 className="font-display text-3xl mt-3 tracking-tight">
              {isAr ? p.nameAr : p.nameEn}
            </h3>
            <p className="mt-5 flex items-baseline gap-1">
              <span className="font-display text-5xl">{p.priceKwd}</span>
              <span className="text-sm text-slate-500">
                {isAr ? "د.ك" : "KWD"} {isAr ? p.periodAr : p.periodEn}
              </span>
            </p>
            <ul className="mt-7 space-y-3 flex-1">
              {(isAr ? p.featuresAr : p.featuresEn).map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm leading-relaxed">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--gold-deep)" }} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <a
              href={`https://wa.me/96569656963?text=${encodeURIComponent(
                isAr ? `أرغب بالاشتراك بخطة "${p.nameAr}"` : `I would like to subscribe to "${p.nameEn}"`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`plan-${p.key}-subscribe`}
              className={`mt-8 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-sm text-sm font-medium transition-colors ${
                p.popular ? "btn-gold" : ""
              }`}
              style={p.popular ? {} : { border: "1px solid var(--line-strong)", color: "var(--ink)" }}
            >
              <Phone className="w-4 h-4" />
              {isAr ? "تواصل للاشتراك" : "Subscribe via WhatsApp"}
            </a>
          </div>
        ))}
      </div>

      <p className="mt-12 text-center text-sm text-slate-500">
        {isAr
          ? "جميع الأسعار بالدينار الكويتي، شاملة ضريبة القيمة المضافة إن وُجدت. الدفع عبر KNET / MyFatoorah / Tap."
          : "All prices in Kuwaiti Dinar, inclusive of VAT where applicable. Payments via KNET / MyFatoorah / Tap."}
      </p>
    </div>
  );
}
